import React, { useState, useEffect } from 'react';
import { 
  User, Mail, Phone, MapPin, Building, Shield, 
  Edit, Save, X, CheckCircle, Clock, XCircle, AlertCircle,
  Wallet, Package, CreditCard, FileText, Upload
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useAuth } from '../../contexts/AuthContext';
import { profileAPI } from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import { Link } from 'react-router-dom';
import api from '../../services/api';

const UserProfileImproved = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [profileData, setProfileData] = useState(null);
  const [kycStatus, setKycStatus] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    business_info: {
      company_name: '',
      gst_number: '',
      pan_number: '',
      business_type: '',
      industry: '',
      cin_number: ''
    }
  });

  useEffect(() => {
    fetchProfileData();
    fetchKYCStatus();
  }, []);

  const fetchProfileData = async () => {
    try {
      const profile = await profileAPI.getProfile();
      setProfileData(profile);
      setFormData({
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        phone: profile.phone || '',
        business_info: profile.business_info || {
          company_name: '',
          gst_number: '',
          pan_number: '',
          business_type: '',
          industry: '',
          cin_number: ''
        }
      });
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      toast({
        title: 'Error',
        description: 'Failed to load profile data',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchKYCStatus = async () => {
    try {
      const response = await api.get('/kyc/status');
      setKycStatus(response.data);
    } catch (error) {
      console.error('Error fetching KYC status:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await profileAPI.updateProfile(formData);
      await fetchProfileData();
      setIsEditing(false);
      toast({
        title: 'Success',
        description: 'Profile updated successfully',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update profile',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value, nested = null) => {
    if (nested) {
      setFormData(prev => ({
        ...prev,
        [nested]: {
          ...prev[nested],
          [field]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const getKYCStatusConfig = () => {
    if (!kycStatus) return null;
    
    switch (kycStatus.status) {
      case 'verified':
        return {
          icon: CheckCircle,
          color: 'text-green-600',
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200',
          label: 'Verified',
          message: 'Your KYC is verified and all features are enabled'
        };
      case 'pending':
      case 'under_review':
        return {
          icon: Clock,
          color: 'text-yellow-600',
          bgColor: 'bg-yellow-50',
          borderColor: 'border-yellow-200',
          label: 'Under Review',
          message: 'Your documents are being reviewed'
        };
      case 'rejected':
        return {
          icon: XCircle,
          color: 'text-red-600',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          label: 'Rejected',
          message: kycStatus.rejection_reason || 'Please resubmit with correct documents'
        };
      default:
        return {
          icon: AlertCircle,
          color: 'text-blue-600',
          bgColor: 'bg-blue-50',
          borderColor: 'border-blue-200',
          label: 'Not Submitted',
          message: 'Complete KYC to enable wallet and COD payments'
        };
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  const kycConfig = getKYCStatusConfig();
  const KYCIcon = kycConfig?.icon;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">My Profile</h1>
        <p className="text-gray-600 mt-1">Manage your account information and preferences</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Profile Summary & KYC */}
        <div className="space-y-6">
          {/* Profile Card */}
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="w-24 h-24 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full mx-auto flex items-center justify-center text-white text-3xl font-bold mb-4">
                  {profileData?.first_name?.[0]}{profileData?.last_name?.[0]}
                </div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {profileData?.first_name} {profileData?.last_name}
                </h2>
                <p className="text-gray-600 mt-1">{profileData?.email}</p>
                <Badge className="mt-3" variant={profileData?.user_type === 'business' ? 'default' : 'secondary'}>
                  {profileData?.user_type === 'business' ? 'Business Account' : 'Individual Account'}
                </Badge>
              </div>

              <div className="mt-6 space-y-3">
                <div className="flex items-center gap-3 text-sm">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600">Email:</span>
                  <span className="font-medium">{profileData?.email}</span>
                  {profileData?.is_email_verified && (
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  )}
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <Phone className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600">Phone:</span>
                  <span className="font-medium">{profileData?.phone}</span>
                  {profileData?.is_phone_verified && (
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* KYC Status Card */}
          {kycConfig && (
            <Card className={`border-2 ${kycConfig.borderColor}`}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Shield className="w-5 h-5" />
                  KYC Verification
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className={`flex items-start gap-3 p-4 rounded-lg ${kycConfig.bgColor}`}>
                  <KYCIcon className={`w-6 h-6 ${kycConfig.color} flex-shrink-0 mt-0.5`} />
                  <div className="flex-1">
                    <div className={`font-semibold ${kycConfig.color}`}>{kycConfig.label}</div>
                    <div className="text-sm text-gray-600 mt-1">{kycConfig.message}</div>
                  </div>
                </div>

                {/* Feature Status */}
                <div className="grid grid-cols-3 gap-2">
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <Package className="w-5 h-5 mx-auto mb-1 text-gray-400" />
                    <div className="text-xs font-medium text-gray-600">Booking</div>
                    <div className={`text-xs font-semibold ${kycStatus?.can_book_shipment ? 'text-green-600' : 'text-gray-400'}`}>
                      {kycStatus?.can_book_shipment ? 'Enabled' : 'Disabled'}
                    </div>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <Wallet className="w-5 h-5 mx-auto mb-1 text-gray-400" />
                    <div className="text-xs font-medium text-gray-600">Wallet</div>
                    <div className={`text-xs font-semibold ${kycStatus?.can_use_wallet ? 'text-green-600' : 'text-gray-400'}`}>
                      {kycStatus?.can_use_wallet ? 'Enabled' : 'Disabled'}
                    </div>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <CreditCard className="w-5 h-5 mx-auto mb-1 text-gray-400" />
                    <div className="text-xs font-medium text-gray-600">COD</div>
                    <div className={`text-xs font-semibold ${kycStatus?.can_use_cod ? 'text-green-600' : 'text-gray-400'}`}>
                      {kycStatus?.can_use_cod ? 'Enabled' : 'Disabled'}
                    </div>
                  </div>
                </div>

                {kycStatus?.status !== 'verified' && (
                  <Link to="/profile/kyc" className="block">
                    <Button className="w-full" variant={kycStatus?.status === 'not_submitted' ? 'default' : 'outline'}>
                      <FileText className="w-4 h-4 mr-2" />
                      {kycStatus?.status === 'not_submitted' ? 'Complete KYC Now' : 'View KYC Details'}
                    </Button>
                  </Link>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Column - Editable Information */}
        <div className="lg:col-span-2 space-y-6">
          {/* Personal Information */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                Personal Information
              </CardTitle>
              {!isEditing ? (
                <Button onClick={() => setIsEditing(true)} variant="outline" size="sm">
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Button onClick={handleSave} size="sm" disabled={isSaving}>
                    <Save className="w-4 h-4 mr-2" />
                    {isSaving ? 'Saving...' : 'Save'}
                  </Button>
                  <Button onClick={() => setIsEditing(false)} variant="outline" size="sm">
                    <X className="w-4 h-4 mr-2" />
                    Cancel
                  </Button>
                </div>
              )}
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    First Name
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={formData.first_name}
                      onChange={(e) => handleInputChange('first_name', e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  ) : (
                    <p className="text-gray-900 p-3 bg-gray-50 rounded-lg">{profileData?.first_name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Last Name
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={formData.last_name}
                      onChange={(e) => handleInputChange('last_name', e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  ) : (
                    <p className="text-gray-900 p-3 bg-gray-50 rounded-lg">{profileData?.last_name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  {isEditing ? (
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  ) : (
                    <p className="text-gray-900 p-3 bg-gray-50 rounded-lg">{profileData?.phone}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <p className="text-gray-900 p-3 bg-gray-100 rounded-lg">{profileData?.email}</p>
                  <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Business Information (if business account) */}
          {profileData?.user_type === 'business' && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building className="w-5 h-5" />
                  Business Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Company Name
                    </label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={formData.business_info.company_name}
                        onChange={(e) => handleInputChange('company_name', e.target.value, 'business_info')}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      />
                    ) : (
                      <p className="text-gray-900 p-3 bg-gray-50 rounded-lg">
                        {profileData?.business_info?.company_name || 'Not provided'}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      GST Number
                    </label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={formData.business_info.gst_number}
                        onChange={(e) => handleInputChange('gst_number', e.target.value.toUpperCase(), 'business_info')}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      />
                    ) : (
                      <p className="text-gray-900 p-3 bg-gray-50 rounded-lg">
                        {profileData?.business_info?.gst_number || 'Not provided'}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      PAN Number
                    </label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={formData.business_info.pan_number}
                        onChange={(e) => handleInputChange('pan_number', e.target.value.toUpperCase(), 'business_info')}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      />
                    ) : (
                      <p className="text-gray-900 p-3 bg-gray-50 rounded-lg">
                        {profileData?.business_info?.pan_number || 'Not provided'}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Industry
                    </label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={formData.business_info.industry}
                        onChange={(e) => handleInputChange('industry', e.target.value, 'business_info')}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      />
                    ) : (
                      <p className="text-gray-900 p-3 bg-gray-50 rounded-lg">
                        {profileData?.business_info?.industry || 'Not provided'}
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserProfileImproved;
